import {
  type FormEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import CoreLayout from "../../components/layout/CoreLayout";
import PageContainer from "../../components/PageContainer";
import PageHeader from "../../components/PageHeader";
import {
  mapRawDocuments,
  parseApiError,
  useKbImport,
  type KbDocument,
} from "../../hooks/useKbImport";
import { handleAuthHttpStatus, isAuthHttpStatus } from "../../lib/authErrors";
import { cardBorderClassName } from "../../lib/cardStyles";
import { formatDateTime } from "../../lib/formatDateTime";
import { usePageTitle } from "../../lib/usePageTitle";

const API_BASE =
  import.meta.env.VITE_API_URL || "https://app.neurospherevoiceai.com/api";

function getCustomerId(): string | null {
  return localStorage.getItem("customerId");
}

type KBCategory = "business_info" | "services" | "faq" | "policies" | "custom";
type KBApprovalStatus = "approved" | "draft" | "rejected" | "processing_failed";
type KBConfidence = "high" | "medium" | "low";

type KBEntry = {
  id: string;
  customer_id: string;
  category: KBCategory;
  question: string;
  answer: string;
  enabled: boolean;
  approval_status: KBApprovalStatus;
  source_document_id: string | null;
  confidence?: KBConfidence | null;
  source_excerpt?: string | null;
  needs_human_review?: boolean | null;
  created_at: string;
  updated_at: string;
};

type KBDocument = KbDocument;

type KBFilter = KBCategory | "all" | "drafts";

const CATEGORY_LABELS: Record<KBFilter, string> = {
  all: "All",
  business_info: "Business Info",
  services: "Services",
  faq: "FAQ",
  policies: "Policies",
  custom: "Custom",
  drafts: "Drafts",
};

const CATEGORY_COLORS: Record<KBCategory, { bg: string; text: string }> = {
  business_info: { bg: "rgba(59,130,246,0.15)", text: "#93c5fd" },
  services: { bg: "rgba(34,197,94,0.15)", text: "#86efac" },
  faq: { bg: "rgba(138,43,226,0.15)", text: "#c084fc" },
  policies: { bg: "rgba(249,115,22,0.15)", text: "#fdba74" },
  custom: { bg: "rgba(107,114,128,0.15)", text: "#d1d5db" },
};

const KB_CATEGORIES: KBCategory[] = [
  "business_info",
  "services",
  "faq",
  "policies",
  "custom",
];

function normalizeApproval(raw: unknown): KBApprovalStatus {
  const s = typeof raw === "string" ? raw.trim().toLowerCase() : "";
  if (
    s === "draft" ||
    s === "rejected" ||
    s === "processing_failed"
  ) {
    return s;
  }
  return "approved";
}

function mapRawEntries(raw: any[], customerId: string): KBEntry[] {
  return raw.map((e) => ({
    id: String(e.id),
    customer_id: String(e.customer_id ?? customerId),
    category: (e.category ?? "custom") as KBCategory,
    question: e.question ?? "",
    answer: e.answer ?? "",
    enabled: e.enabled ?? true,
    approval_status: normalizeApproval(e.approval_status),
    source_document_id: e.source_document_id
      ? String(e.source_document_id)
      : null,
    confidence: e.confidence ?? null,
    source_excerpt: e.source_excerpt ?? null,
    needs_human_review: e.needs_human_review ?? null,
    created_at: e.created_at ?? "",
    updated_at: e.updated_at ?? "",
  }));
}

function isDraft(entry: KBEntry): boolean {
  return entry.approval_status === "draft";
}

function visibleEntries(entries: KBEntry[]): KBEntry[] {
  return entries.filter((e) => e.approval_status !== "rejected");
}

function categoryCounts(entries: KBEntry[], cat: KBFilter) {
  const visible = visibleEntries(entries);
  if (cat === "drafts") {
    const draftEntries = visible.filter((e) => isDraft(e));
    return { total: draftEntries.length, drafts: draftEntries.length };
  }
  const inCat =
    cat === "all" ? visible : visible.filter((e) => e.category === cat);
  const drafts = inCat.filter((e) => isDraft(e)).length;
  return { total: inCat.length, drafts };
}

function sortDraftsFirst(entries: KBEntry[]): KBEntry[] {
  return [...entries].sort((a, b) => {
    const aDraft = isDraft(a) ? 0 : 1;
    const bDraft = isDraft(b) ? 0 : 1;
    return aDraft - bDraft;
  });
}

function SectionCard({
  title,
  description,
  icon,
  primaryActionLabel,
  onPrimaryAction,
  secondaryActionLabel,
  onSecondaryAction,
  children,
}: {
  title: string;
  description: string;
  icon: string;
  primaryActionLabel: string;
  onPrimaryAction: () => void;
  secondaryActionLabel?: string;
  onSecondaryAction?: () => void;
  children: React.ReactNode;
}) {
  return (
    <section
      className={`rounded-xl backdrop-blur-sm overflow-hidden ${cardBorderClassName}`}
      style={{ background: "rgba(28, 28, 37, 0.8)" }}
    >
      <div className="p-5">
        <div className="flex flex-wrap items-start justify-between gap-4 mb-5">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <i className={`${icon} text-purple-400 text-xl`} />
              <h2
                className="text-xl font-semibold text-white"
                style={{ fontFamily: "'Space Grotesk', sans-serif" }}
              >
                {title}
              </h2>
            </div>
            <p
              className="text-gray-400 text-sm"
              style={{ fontFamily: "'Space Grotesk', sans-serif" }}
            >
              {description}
            </p>
          </div>

          <div className="flex flex-wrap gap-2">
            {secondaryActionLabel && onSecondaryAction ? (
              <button
                type="button"
                onClick={onSecondaryAction}
                className="px-4 py-2 rounded-lg text-sm font-semibold text-white cursor-pointer"
                style={{
                  background: "rgba(255,255,255,0.08)",
                  border: "1px solid rgba(138, 43, 226, 0.35)",
                  fontFamily: "'Space Grotesk', sans-serif",
                }}
              >
                {secondaryActionLabel}
              </button>
            ) : null}
            <button
              type="button"
              onClick={onPrimaryAction}
              className="px-4 py-2 rounded-lg text-sm font-semibold text-white cursor-pointer"
              style={{
                background: "linear-gradient(90deg, #8a2be2, #ff6a00)",
                fontFamily: "'Space Grotesk', sans-serif",
              }}
            >
              {primaryActionLabel}
            </button>
          </div>
        </div>

        {children}
      </div>
    </section>
  );
}

function FieldLabel({ children }: { children: React.ReactNode }) {
  return (
    <label
      className="block text-xs text-gray-400 mb-1"
      style={{ fontFamily: "'Space Grotesk', sans-serif" }}
    >
      {children}
    </label>
  );
}

export default function KnowledgeBase() {
  usePageTitle("Knowledge Base");

  const [entries, setEntries] = useState<KBEntry[]>([]);
  const [entriesLoading, setEntriesLoading] = useState(true);
  const [entriesError, setEntriesError] = useState(false);
  const [documents, setDocuments] = useState<KBDocument[]>([]);
  const [documentsLoading, setDocumentsLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState<KBFilter>("all");
  const entriesListRef = useRef<HTMLDivElement>(null);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [sourceExpandedIds, setSourceExpandedIds] = useState<Set<string>>(
    new Set()
  );
  const [docErrorExpanded, setDocErrorExpanded] = useState<Set<string>>(
    new Set()
  );
  const [statusMessage, setStatusMessage] = useState<string | null>(null);

  const flashStatus = (msg: string) => {
    setStatusMessage(msg);
    window.setTimeout(() => setStatusMessage(null), 3500);
  };

  const fetchEntries = useCallback(async (opts?: { silent?: boolean }) => {
    const customerId = getCustomerId();
    if (!customerId) {
      if (!opts?.silent) {
        setEntriesLoading(false);
        setEntriesError(true);
      }
      return false;
    }
    if (!opts?.silent) {
      setEntriesLoading(true);
      setEntriesError(false);
    }
    try {
      const res = await fetch(
        `${API_BASE}/customers/${customerId}/kb?include_drafts=1`,
        { credentials: "include" }
      );
      if (isAuthHttpStatus(res.status)) {
        void handleAuthHttpStatus(res.status, () => setEntriesError(true));
        return false;
      }
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      const raw: any[] = Array.isArray(data) ? data : (data.entries ?? []);
      setEntries(mapRawEntries(raw, customerId));
      return true;
    } catch {
      if (!opts?.silent) setEntriesError(true);
      return false;
    } finally {
      if (!opts?.silent) setEntriesLoading(false);
    }
  }, []);

  const fetchDocuments = useCallback(async (opts?: { silent?: boolean }) => {
    const customerId = getCustomerId();
    if (!customerId) {
      if (!opts?.silent) setDocumentsLoading(false);
      return false;
    }
    if (!opts?.silent) setDocumentsLoading(true);
    try {
      const res = await fetch(
        `${API_BASE}/customers/${customerId}/kb/documents`,
        { credentials: "include" }
      );
      if (isAuthHttpStatus(res.status)) {
        void handleAuthHttpStatus(res.status, () => {});
        return false;
      }
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      const raw: any[] = Array.isArray(data)
        ? data
        : (data.documents ?? []);
      setDocuments(mapRawDocuments(raw));
      return true;
    } catch {
      return false;
    } finally {
      if (!opts?.silent) setDocumentsLoading(false);
    }
  }, []);

  const kb = useKbImport({
    onDocumentsChanged: () => fetchDocuments({ silent: true }),
    onEntriesChanged: () => fetchEntries({ silent: true }),
  });

  useEffect(() => {
    void fetchEntries();
    void fetchDocuments();
  }, [fetchEntries, fetchDocuments]);

  const [showArticleForm, setShowArticleForm] = useState(false);
  const [articleTitle, setArticleTitle] = useState("");
  const [articleBody, setArticleBody] = useState("");
  const [articleCategory, setArticleCategory] = useState<KBCategory>("custom");
  const [saving, setSaving] = useState(false);

  const [editingEntryId, setEditingEntryId] = useState<string | null>(null);
  const [editTitle, setEditTitle] = useState("");
  const [editBody, setEditBody] = useState("");
  const [editCategory, setEditCategory] = useState<KBCategory>("custom");
  const [editSaving, setEditSaving] = useState(false);

  const displayEntries = useMemo(() => visibleEntries(entries), [entries]);

  const draftCount = useMemo(
    () => displayEntries.filter((e) => isDraft(e)).length,
    [displayEntries]
  );

  useEffect(() => {
    if (activeCategory === "drafts" && draftCount === 0) {
      setActiveCategory("all");
    }
  }, [activeCategory, draftCount]);

  const reviewDrafts = () => {
    setActiveCategory("drafts");
    entriesListRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const addArticle = async (e: FormEvent) => {
    e.preventDefault();
    const question = articleTitle.trim();
    const answer = articleBody.trim();
    if (!question || !answer) return;

    const customerId = getCustomerId();
    if (!customerId) {
      flashStatus("No customer ID found.");
      return;
    }

    setSaving(true);
    try {
      const res = await fetch(`${API_BASE}/customers/${customerId}/kb`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          category: articleCategory,
          question,
          answer,
          enabled: true,
        }),
      });
      if (isAuthHttpStatus(res.status)) {
        void handleAuthHttpStatus(res.status, () => {
          flashStatus("Your session expired. Please log in again.");
        });
        return;
      }
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        flashStatus(
          `Save failed: HTTP ${res.status}${parseApiError(data) ? ` — ${parseApiError(data)}` : ""}`
        );
        return;
      }
      setArticleTitle("");
      setArticleBody("");
      setArticleCategory("custom");
      setShowArticleForm(false);
      flashStatus("Article saved.");
      await fetchEntries({ silent: true });
    } catch (err: unknown) {
      flashStatus(err instanceof Error ? err.message : "Failed to save article.");
    } finally {
      setSaving(false);
    }
  };

  const startEditEntry = (entry: KBEntry) => {
    setEditingEntryId(entry.id);
    setEditTitle(entry.question);
    setEditBody(entry.answer);
    setEditCategory(entry.category);
  };

  const cancelEditEntry = () => {
    setEditingEntryId(null);
    setEditTitle("");
    setEditBody("");
    setEditCategory("custom");
  };

  const saveEditEntry = async (entryId: string) => {
    const question = editTitle.trim();
    const answer = editBody.trim();
    if (!question || !answer) return;

    const customerId = getCustomerId();
    if (!customerId) {
      flashStatus("No customer ID found.");
      return;
    }

    setEditSaving(true);
    try {
      const res = await fetch(
        `${API_BASE}/customers/${customerId}/kb/${entryId}`,
        {
          method: "PUT",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            question,
            answer,
            category: editCategory,
          }),
        }
      );
      if (isAuthHttpStatus(res.status)) {
        void handleAuthHttpStatus(res.status, () => {
          flashStatus("Your session expired. Please log in again.");
        });
        return;
      }
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        flashStatus(
          `Update failed: HTTP ${res.status}${parseApiError(data) ? ` — ${parseApiError(data)}` : ""}`
        );
        return;
      }
      cancelEditEntry();
      flashStatus("Entry updated.");
      await fetchEntries({ silent: true });
    } catch {
      flashStatus("Failed to update entry.");
    } finally {
      setEditSaving(false);
    }
  };

  const toggleEnabled = async (entryId: string) => {
    const previous = entries.find((e) => e.id === entryId);
    if (!previous || isDraft(previous)) return;
    const nextEnabled = !previous.enabled;

    setEntries((prev) =>
      prev.map((e) => (e.id === entryId ? { ...e, enabled: nextEnabled } : e))
    );

    const customerId = getCustomerId();
    if (!customerId) {
      setEntries((prev) =>
        prev.map((e) =>
          e.id === entryId ? { ...e, enabled: previous.enabled } : e
        )
      );
      flashStatus("No customer ID found.");
      return;
    }

    try {
      const res = await fetch(
        `${API_BASE}/customers/${customerId}/kb/${entryId}`,
        {
          method: "PUT",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ enabled: nextEnabled }),
        }
      );
      if (isAuthHttpStatus(res.status)) {
        void handleAuthHttpStatus(res.status, () => {
          setEntries((prev) =>
            prev.map((e) =>
              e.id === entryId ? { ...e, enabled: previous.enabled } : e
            )
          );
        });
        return;
      }
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        flashStatus(
          `Update failed: HTTP ${res.status}${parseApiError(data) ? ` — ${parseApiError(data)}` : ""}`
        );
        setEntries((prev) =>
          prev.map((e) =>
            e.id === entryId ? { ...e, enabled: previous.enabled } : e
          )
        );
        return;
      }
      await fetchEntries({ silent: true });
    } catch {
      setEntries((prev) =>
        prev.map((e) =>
          e.id === entryId ? { ...e, enabled: previous.enabled } : e
        )
      );
      flashStatus("Failed to update entry.");
    }
  };

  const setApprovalStatus = async (
    entryId: string,
    approval_status: "approved" | "rejected"
  ) => {
    const previous = entries.find((e) => e.id === entryId);
    if (!previous) return;

    setEntries((prev) =>
      prev
        .map((e): KBEntry => {
          if (e.id !== entryId) return e;
          if (approval_status === "approved") {
            return {
              ...e,
              approval_status: "approved",
              enabled: true,
            };
          }
          return { ...e, approval_status: "rejected", enabled: false };
        })
        .filter((e) => e.approval_status !== "rejected")
    );

    const customerId = getCustomerId();
    if (!customerId) {
      await fetchEntries({ silent: true });
      flashStatus("No customer ID found.");
      return;
    }

    try {
      const res = await fetch(
        `${API_BASE}/customers/${customerId}/kb/${entryId}`,
        {
          method: "PUT",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ approval_status }),
        }
      );
      if (isAuthHttpStatus(res.status)) {
        void handleAuthHttpStatus(res.status, () => {
          void fetchEntries({ silent: true });
        });
        return;
      }
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        flashStatus(
          `Update failed: HTTP ${res.status}${parseApiError(data) ? ` — ${parseApiError(data)}` : ""}`
        );
        await fetchEntries({ silent: true });
        return;
      }
      flashStatus(
        approval_status === "approved" ? "Entry approved." : "Entry rejected."
      );
      await fetchEntries({ silent: true });
    } catch {
      await fetchEntries({ silent: true });
      flashStatus("Failed to update entry.");
    }
  };

  const deleteEntry = async (entryId: string) => {
    if (
      !window.confirm(
        "Delete this knowledge base entry? This cannot be undone."
      )
    ) {
      return;
    }

    const customerId = getCustomerId();
    if (!customerId) {
      flashStatus("No customer ID found.");
      return;
    }

    try {
      const res = await fetch(
        `${API_BASE}/customers/${customerId}/kb/${entryId}`,
        {
          method: "DELETE",
          credentials: "include",
        }
      );
      if (isAuthHttpStatus(res.status)) {
        void handleAuthHttpStatus(res.status, () => {
          flashStatus("Your session expired. Please log in again.");
        });
        return;
      }
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        flashStatus(
          `Delete failed: HTTP ${res.status}${parseApiError(data) ? ` — ${parseApiError(data)}` : ""}`
        );
        return;
      }
      flashStatus("Entry deleted.");
      await fetchEntries({ silent: true });
    } catch (err: unknown) {
      flashStatus(err instanceof Error ? err.message : "Failed to delete entry.");
    }
  };

  const deleteDocument = async (docId: string) => {
    if (
      !window.confirm(
        "Delete this document? This also discards its unapproved drafts."
      )
    ) {
      return;
    }

    const customerId = getCustomerId();
    if (!customerId) {
      flashStatus("No customer ID found.");
      return;
    }

    try {
      const res = await fetch(
        `${API_BASE}/customers/${customerId}/kb/documents/${docId}`,
        { method: "DELETE", credentials: "include" }
      );
      if (isAuthHttpStatus(res.status)) {
        void handleAuthHttpStatus(res.status, () => {
          flashStatus("Your session expired. Please log in again.");
        });
        return;
      }
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        flashStatus(
          `Delete failed: HTTP ${res.status}${parseApiError(data) ? ` — ${parseApiError(data)}` : ""}`
        );
        return;
      }
      flashStatus("Document deleted.");
      await fetchDocuments({ silent: true });
      await fetchEntries({ silent: true });
    } catch {
      flashStatus("Failed to delete document.");
    }
  };

  const toggleDocError = (docId: string) => {
    setDocErrorExpanded((prev) => {
      const next = new Set(prev);
      next.has(docId) ? next.delete(docId) : next.add(docId);
      return next;
    });
  };

  return (
    <CoreLayout>
      <PageContainer>
        <PageHeader
          title="Knowledge Base"
          subtitle="Give your AI the business knowledge it needs to answer confidently"
        />

        {statusMessage && (
          <div
            className="mb-4 px-4 py-3 rounded-lg text-sm text-center"
            style={{
              background: "rgba(138, 43, 226, 0.15)",
              border: "1px solid rgba(138, 43, 226, 0.35)",
              color: "rgba(255,255,255,0.9)",
              fontFamily: "'Space Grotesk', sans-serif",
            }}
          >
            {statusMessage}
          </div>
        )}

        <div className="space-y-6">
          <SectionCard
            title="Articles"
            description="Long-form business knowledge your AI can reference in conversation."
            icon="ri-article-line"
            primaryActionLabel="Add Article"
            onPrimaryAction={() => setShowArticleForm((open) => !open)}
            secondaryActionLabel="Upload Document"
            onSecondaryAction={() => kb.fileInputRef.current?.click()}
          >
            <input
              ref={kb.fileInputRef}
              type="file"
              accept=".pdf,.txt,.docx,application/pdf,text/plain,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
              className="hidden"
              onChange={kb.onFileInputChange}
              disabled={kb.uploading}
            />

            <div
              onDragOver={(e) => {
                e.preventDefault();
                if (!kb.uploading) kb.setUploadDragOver(true);
              }}
              onDragLeave={() => kb.setUploadDragOver(false)}
              onDrop={kb.onDrop}
              className={`mb-5 rounded-xl border-2 border-dashed p-6 text-center transition-colors ${
                kb.uploading ? "opacity-60 pointer-events-none" : "cursor-pointer"
              }`}
              style={{
                borderColor: kb.uploadDragOver
                  ? "rgba(138, 43, 226, 0.65)"
                  : "rgba(138, 43, 226, 0.25)",
                background: kb.uploadDragOver
                  ? "rgba(138, 43, 226, 0.08)"
                  : "rgba(13, 13, 18, 0.5)",
              }}
              onClick={() => {
                if (!kb.uploading) kb.fileInputRef.current?.click();
              }}
            >
              {kb.uploading ? (
                <div className="flex flex-col items-center gap-3">
                  <i className="ri-loader-4-line text-3xl text-purple-400 animate-spin" />
                  <p
                    className="text-gray-300 text-sm"
                    style={{ fontFamily: "'Space Grotesk', sans-serif" }}
                  >
                    Reading your document… this can take up to a minute.
                  </p>
                </div>
              ) : (
                <>
                  <i className="ri-upload-cloud-2-line text-3xl text-purple-400/80 mb-2" />
                  <p
                    className="text-gray-300 text-sm"
                    style={{ fontFamily: "'Space Grotesk', sans-serif" }}
                  >
                    Drag and drop a PDF, DOCX, or TXT file here, or click to
                    browse
                  </p>
                  <p
                    className="text-gray-500 text-xs mt-1"
                    style={{ fontFamily: "'Space Grotesk', sans-serif" }}
                  >
                    Max 10MB
                  </p>
                </>
              )}
            </div>

            <div className="mb-5">
              <div className="flex flex-wrap items-center gap-2">
                <input
                  type="url"
                  value={kb.urlValue}
                  onChange={(e) => {
                    kb.setUrlValue(e.target.value);
                    if (kb.urlError) kb.setUrlError(null);
                  }}
                  onKeyDown={kb.onUrlInputKeyDown}
                  placeholder="https://yourbusiness.com"
                  disabled={kb.urlSubmitting}
                  className="flex-1 min-w-[200px] px-3 py-2 rounded-lg bg-black/30 border border-purple-500/20 text-white text-sm focus:outline-none focus:border-purple-500/50 disabled:opacity-50"
                  style={{ fontFamily: "'Space Grotesk', sans-serif" }}
                />
                <button
                  type="button"
                  onClick={() => void kb.importFromUrl()}
                  disabled={kb.urlSubmitting}
                  className="px-4 py-2 rounded-lg text-sm font-medium text-gray-300 cursor-pointer disabled:opacity-50 whitespace-nowrap"
                  style={{
                    background: "rgba(255,255,255,0.05)",
                    border: "1px solid rgba(138, 43, 226, 0.2)",
                    fontFamily: "'Space Grotesk', sans-serif",
                  }}
                >
                  {kb.urlSubmitting ? "Importing…" : "Import from URL"}
                </button>
              </div>
              {kb.urlError ? (
                <p
                  className="mt-1.5 text-xs text-red-300"
                  style={{ fontFamily: "'Space Grotesk', sans-serif" }}
                >
                  {kb.urlError}
                </p>
              ) : null}
            </div>

            {kb.uploadInlineError ? (
              <p
                className="mb-4 text-sm text-red-300"
                style={{ fontFamily: "'Space Grotesk', sans-serif" }}
              >
                {kb.uploadInlineError}
              </p>
            ) : null}

            {kb.uploadResult ? (
              <div
                className="mb-5 px-4 py-3 rounded-lg text-sm"
                style={{
                  fontFamily: "'Space Grotesk', sans-serif",
                  background:
                    kb.uploadResult.kind === "success"
                      ? "rgba(34, 197, 94, 0.12)"
                      : "rgba(239, 68, 68, 0.12)",
                  border:
                    kb.uploadResult.kind === "success"
                      ? "1px solid rgba(34, 197, 94, 0.35)"
                      : "1px solid rgba(239, 68, 68, 0.35)",
                  color:
                    kb.uploadResult.kind === "success" ? "#bbf7d0" : "#fecaca",
                }}
              >
                {kb.uploadResult.message}
              </div>
            ) : null}

            {documentsLoading ? (
              <p
                className="text-gray-500 text-xs mb-5"
                style={{ fontFamily: "'Space Grotesk', sans-serif" }}
              >
                Loading documents…
              </p>
            ) : documents.length === 0 ? (
              <p
                className="text-gray-500 text-sm mb-5"
                style={{ fontFamily: "'Space Grotesk', sans-serif" }}
              >
                Upload a menu, price sheet, or policy document and we&apos;ll
                draft entries from it.
              </p>
            ) : (
              <div className="space-y-2 mb-5">
                {documents.map((doc) => {
                  const isFailed = doc.status === "failed";
                  const isReady = doc.status === "ready";
                  const showError = docErrorExpanded.has(doc.id);
                  return (
                    <div
                      key={doc.id}
                      className={`rounded-lg px-4 py-3 flex flex-wrap items-center justify-between gap-3 ${cardBorderClassName}`}
                      style={{ background: "rgba(13, 13, 18, 0.55)" }}
                    >
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 min-w-0">
                          <p
                            className="text-white text-sm font-medium truncate"
                            style={{ fontFamily: "'Space Grotesk', sans-serif" }}
                          >
                            {doc.filename}
                          </p>
                          {doc.source_type === "url" ? (
                            <span
                              className="shrink-0 inline-flex items-center gap-0.5 text-[10px] font-medium px-1.5 py-0.5 rounded-full"
                              style={{
                                background: "rgba(107, 114, 128, 0.2)",
                                color: "#9ca3af",
                              }}
                              title="Imported from URL"
                            >
                              <i className="ri-global-line text-[10px]" />
                              URL
                            </span>
                          ) : null}
                        </div>
                        <p
                          className="text-gray-500 text-xs mt-0.5"
                          style={{ fontFamily: "'Space Grotesk', sans-serif" }}
                        >
                          {formatDateTime(doc.created_at) || "—"}
                        </p>
                      </div>
                      <div className="flex items-center gap-2 flex-wrap">
                        {isReady ? (
                          <span
                            className="text-xs font-semibold px-2 py-0.5 rounded-full"
                            style={{
                              background: "rgba(34, 197, 94, 0.15)",
                              color: "#86efac",
                            }}
                          >
                            Ready
                            {doc.entries_created > 0
                              ? ` +${doc.entries_created} entries`
                              : " · 0 entries"}
                          </span>
                        ) : isFailed ? (
                          <button
                            type="button"
                            onClick={() => toggleDocError(doc.id)}
                            className="text-xs font-semibold px-2 py-0.5 rounded-full cursor-pointer"
                            style={{
                              background: "rgba(239, 68, 68, 0.15)",
                              color: "#fca5a5",
                            }}
                          >
                            Failed
                            {doc.error_text ? " ▾" : ""}
                          </button>
                        ) : (
                          <span
                            className="text-xs font-semibold px-2 py-0.5 rounded-full"
                            style={{
                              background: "rgba(107, 114, 128, 0.2)",
                              color: "#d1d5db",
                            }}
                          >
                            {doc.status}
                          </span>
                        )}
                        <button
                          type="button"
                          onClick={() => deleteDocument(doc.id)}
                          className="p-1.5 rounded-lg text-gray-500 hover:text-red-400 transition-colors cursor-pointer"
                          style={{ background: "rgba(255,255,255,0.04)" }}
                          title="Delete document"
                        >
                          <i className="ri-delete-bin-line text-sm" />
                        </button>
                      </div>
                      {isFailed && showError && doc.error_text ? (
                        <p
                          className="w-full text-xs text-red-200/90 mt-1 whitespace-pre-wrap"
                          style={{ fontFamily: "'Space Grotesk', sans-serif" }}
                        >
                          {doc.error_text}
                        </p>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            )}

            {draftCount > 0 ? (
              <div
                className="mb-5 flex flex-wrap items-center justify-between gap-3 px-4 py-3 rounded-lg"
                style={{
                  background: "rgba(245, 158, 11, 0.12)",
                  border: "1px solid rgba(245, 158, 11, 0.25)",
                  fontFamily: "'Space Grotesk', sans-serif",
                }}
              >
                <p className="text-sm text-amber-100/90">
                  {draftCount} draft {draftCount === 1 ? "entry" : "entries"}{" "}
                  pending review
                </p>
                <button
                  type="button"
                  onClick={reviewDrafts}
                  className="px-3 py-1.5 rounded-lg text-xs font-semibold text-white cursor-pointer shrink-0"
                  style={{
                    background: "rgba(245, 158, 11, 0.35)",
                    border: "1px solid rgba(245, 158, 11, 0.45)",
                    fontFamily: "'Space Grotesk', sans-serif",
                  }}
                >
                  Review drafts
                </button>
              </div>
            ) : null}

            {showArticleForm && (
              <form
                onSubmit={addArticle}
                className={`mb-5 p-4 rounded-xl ${cardBorderClassName}`}
                style={{ background: "rgba(26, 26, 36, 0.75)" }}
              >
                <div className="mb-3">
                  <FieldLabel>Title</FieldLabel>
                  <input
                    type="text"
                    value={articleTitle}
                    onChange={(e) => setArticleTitle(e.target.value)}
                    placeholder="e.g. Services overview"
                    maxLength={1000}
                    className="w-full px-3 py-2 rounded-lg bg-black/40 border border-purple-500/30 text-white text-sm focus:outline-none focus:border-purple-500"
                  />
                </div>
                <div className="mb-3">
                  <FieldLabel>Content</FieldLabel>
                  <textarea
                    value={articleBody}
                    onChange={(e) => setArticleBody(e.target.value)}
                    placeholder="Add details your AI should know..."
                    rows={5}
                    maxLength={1000}
                    className="w-full px-3 py-2 rounded-lg bg-black/40 border border-purple-500/30 text-white text-sm focus:outline-none focus:border-purple-500 resize-y"
                  />
                </div>
                <div className="mb-3">
                  <FieldLabel>Category</FieldLabel>
                  <select
                    value={articleCategory}
                    onChange={(e) =>
                      setArticleCategory(e.target.value as KBCategory)
                    }
                    className="w-full px-3 py-2 rounded-lg bg-black/40 border border-purple-500/30 text-white text-sm focus:outline-none focus:border-purple-500"
                  >
                    {KB_CATEGORIES.map((cat) => (
                      <option key={cat} value={cat}>
                        {CATEGORY_LABELS[cat]}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="flex gap-2">
                  <button
                    type="submit"
                    disabled={saving}
                    className="px-4 py-2 rounded-lg text-sm font-semibold text-white cursor-pointer disabled:opacity-50"
                    style={{
                      background: "linear-gradient(90deg, #8a2be2, #ff6a00)",
                    }}
                  >
                    {saving ? "Saving…" : "Save article"}
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowArticleForm(false)}
                    disabled={saving}
                    className="px-4 py-2 rounded-lg text-sm text-gray-300 cursor-pointer"
                    style={{
                      background: "rgba(255,255,255,0.06)",
                      border: "1px solid rgba(138, 43, 226, 0.2)",
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            )}

            <div className="flex flex-wrap gap-2 mb-5">
              {(["all", ...KB_CATEGORIES, "drafts"] as const).map((cat) => {
                const { total, drafts } = categoryCounts(displayEntries, cat);
                const isActive = activeCategory === cat;
                return (
                  <button
                    key={cat}
                    type="button"
                    onClick={() => setActiveCategory(cat)}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-all duration-200 cursor-pointer"
                    style={{
                      fontFamily: "'Space Grotesk', sans-serif",
                      background: isActive
                        ? "linear-gradient(90deg, #8a2be2, #ff6a00)"
                        : "rgba(255,255,255,0.05)",
                      color: isActive ? "#fff" : "#aaaaaa",
                      border: isActive
                        ? "1px solid transparent"
                        : "1px solid rgba(138,43,226,0.2)",
                    }}
                  >
                    {CATEGORY_LABELS[cat]}
                    {total > 0 && (
                      <span
                        className="text-xs px-1.5 py-0.5 rounded-full"
                        style={{
                          background: isActive
                            ? "rgba(255,255,255,0.2)"
                            : "rgba(138,43,226,0.2)",
                          color: isActive ? "#fff" : "#c084fc",
                        }}
                      >
                        {total}
                        {cat !== "drafts" && drafts > 0
                          ? ` (${drafts} drafts)`
                          : ""}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>

            <div ref={entriesListRef}>
            {entriesLoading ? (
              <div className="text-center py-8 mb-4">
                <p className="text-gray-400 text-sm">Loading knowledge base…</p>
              </div>
            ) : entriesError ? (
              <div className="text-center py-8 mb-4">
                <p className="text-red-300 text-sm">
                  Couldn&apos;t load knowledge base entries.
                </p>
              </div>
            ) : displayEntries.length === 0 ? (
              <div className="text-center py-12">
                <i className="ri-file-text-line text-5xl text-purple-400/30 mb-5 block" />
                <p className="text-gray-400">
                  No articles yet. Add your first article or upload a document
                  to draft entries from it.
                </p>
              </div>
            ) : (
              (() => {
                let filtered: KBEntry[];
                if (activeCategory === "drafts") {
                  filtered = displayEntries.filter((e) => isDraft(e));
                } else if (activeCategory === "all") {
                  filtered = sortDraftsFirst(displayEntries);
                } else {
                  filtered = displayEntries.filter(
                    (e) => e.category === activeCategory
                  );
                }

                if (filtered.length === 0) {
                  return (
                    <div className="text-center py-8 mb-4">
                      <p className="text-gray-500 text-sm">
                        {activeCategory === "drafts"
                          ? "No draft entries."
                          : `No ${CATEGORY_LABELS[activeCategory].toLowerCase()} entries yet.`}
                      </p>
                    </div>
                  );
                }

                return (
                  <div className="space-y-3 mb-5">
                    {filtered.map((entry) => {
                      const draft = isDraft(entry);
                      const isExpanded = expandedIds.has(entry.id);
                      const sourceOpen = sourceExpandedIds.has(entry.id);
                      const truncated =
                        entry.answer.length > 120 && !isExpanded;
                      const catColors = CATEGORY_COLORS[entry.category];
                      const updatedLabel = formatDateTime(entry.updated_at);
                      const isEditing = editingEntryId === entry.id;

                      const confidence = entry.confidence;
                      const showConfidence =
                        draft && confidence && ["high", "medium", "low"].includes(confidence);
                      const lowConfidence =
                        confidence === "low" || entry.needs_human_review;

                      return (
                        <div
                          key={entry.id}
                          className={`rounded-xl p-4 transition-opacity duration-200 ${cardBorderClassName}`}
                          style={{
                            background: "rgba(13, 13, 18, 0.7)",
                            opacity: draft ? 1 : entry.enabled ? 1 : 0.5,
                          }}
                        >
                          <div className="flex flex-wrap items-center gap-2 mb-2">
                            <span
                              className="text-xs font-semibold px-2 py-0.5 rounded-full"
                              style={{
                                background: catColors.bg,
                                color: catColors.text,
                              }}
                            >
                              {CATEGORY_LABELS[entry.category]}
                            </span>
                            {draft ? (
                              <span
                                className="text-xs font-semibold px-2 py-0.5 rounded-full"
                                style={{
                                  background: "rgba(245, 158, 11, 0.18)",
                                  color: "#fcd34d",
                                }}
                              >
                                Draft
                              </span>
                            ) : null}
                            {showConfidence ? (
                              <span
                                className="text-xs font-medium px-2 py-0.5 rounded-full"
                                style={{
                                  background: lowConfidence
                                    ? "rgba(249, 115, 22, 0.15)"
                                    : "rgba(107, 114, 128, 0.2)",
                                  color: lowConfidence ? "#fdba74" : "#d1d5db",
                                }}
                              >
                                {confidence} confidence
                              </span>
                            ) : null}
                            <span className="text-xs text-gray-500 ml-auto">
                              {updatedLabel || "—"}
                            </span>
                          </div>

                          {isEditing ? (
                            <div className="space-y-3 mb-3">
                              <div>
                                <FieldLabel>Title</FieldLabel>
                                <input
                                  type="text"
                                  value={editTitle}
                                  onChange={(e) => setEditTitle(e.target.value)}
                                  className="w-full px-3 py-2 rounded-lg bg-black/40 border border-purple-500/30 text-white text-sm"
                                />
                              </div>
                              <div>
                                <FieldLabel>Content</FieldLabel>
                                <textarea
                                  value={editBody}
                                  onChange={(e) => setEditBody(e.target.value)}
                                  rows={4}
                                  className="w-full px-3 py-2 rounded-lg bg-black/40 border border-purple-500/30 text-white text-sm resize-y"
                                />
                              </div>
                              <div>
                                <FieldLabel>Category</FieldLabel>
                                <select
                                  value={editCategory}
                                  onChange={(e) =>
                                    setEditCategory(e.target.value as KBCategory)
                                  }
                                  className="w-full px-3 py-2 rounded-lg bg-black/40 border border-purple-500/30 text-white text-sm"
                                >
                                  {KB_CATEGORIES.map((cat) => (
                                    <option key={cat} value={cat}>
                                      {CATEGORY_LABELS[cat]}
                                    </option>
                                  ))}
                                </select>
                              </div>
                              <div className="flex gap-2">
                                <button
                                  type="button"
                                  disabled={editSaving}
                                  onClick={() => saveEditEntry(entry.id)}
                                  className="px-3 py-1.5 rounded-lg text-xs font-semibold text-white"
                                  style={{
                                    background:
                                      "linear-gradient(90deg, #8a2be2, #ff6a00)",
                                  }}
                                >
                                  {editSaving ? "Saving…" : "Save"}
                                </button>
                                <button
                                  type="button"
                                  onClick={cancelEditEntry}
                                  className="px-3 py-1.5 rounded-lg text-xs text-gray-300"
                                  style={{
                                    background: "rgba(255,255,255,0.06)",
                                  }}
                                >
                                  Cancel
                                </button>
                              </div>
                            </div>
                          ) : (
                            <>
                              <p className="text-white font-semibold text-sm mb-1">
                                {entry.question}
                              </p>
                              <p className="text-gray-400 text-sm leading-relaxed">
                                {truncated
                                  ? entry.answer.slice(0, 120) + "…"
                                  : entry.answer}
                              </p>
                              {entry.answer.length > 120 && (
                                <button
                                  type="button"
                                  onClick={() =>
                                    setExpandedIds((prev) => {
                                      const next = new Set(prev);
                                      next.has(entry.id)
                                        ? next.delete(entry.id)
                                        : next.add(entry.id);
                                      return next;
                                    })
                                  }
                                  className="mt-1 text-xs text-purple-400 hover:text-purple-300 cursor-pointer"
                                >
                                  {isExpanded ? "Show less" : "Show more"}
                                </button>
                              )}
                            </>
                          )}

                          {draft && entry.source_excerpt && !isEditing ? (
                            <div className="mt-2">
                              <button
                                type="button"
                                onClick={() =>
                                  setSourceExpandedIds((prev) => {
                                    const next = new Set(prev);
                                    next.has(entry.id)
                                      ? next.delete(entry.id)
                                      : next.add(entry.id);
                                    return next;
                                  })
                                }
                                className="text-xs text-gray-500 hover:text-gray-300 cursor-pointer"
                              >
                                {sourceOpen ? "Hide source" : "Source excerpt"}{" "}
                                ▾
                              </button>
                              {sourceOpen ? (
                                <p className="text-xs text-gray-500 mt-1 italic">
                                  Source: &ldquo;{entry.source_excerpt}&rdquo;
                                </p>
                              ) : null}
                            </div>
                          ) : null}

                          <div className="flex items-center justify-between mt-3">
                            {draft ? (
                              <div className="flex items-center gap-2">
                                <button
                                  type="button"
                                  onClick={() =>
                                    setApprovalStatus(entry.id, "approved")
                                  }
                                  className="px-3 py-1.5 rounded-lg text-xs font-semibold text-white cursor-pointer"
                                  style={{
                                    background:
                                      "linear-gradient(90deg, #8a2be2, #ff6a00)",
                                  }}
                                >
                                  Approve
                                </button>
                                <button
                                  type="button"
                                  onClick={() =>
                                    setApprovalStatus(entry.id, "rejected")
                                  }
                                  className="px-3 py-1.5 rounded-lg text-xs text-red-300/90 cursor-pointer hover:text-red-200"
                                  style={{
                                    background: "rgba(239, 68, 68, 0.08)",
                                    border:
                                      "1px solid rgba(239, 68, 68, 0.2)",
                                  }}
                                >
                                  Reject
                                </button>
                              </div>
                            ) : (
                              <button
                                type="button"
                                onClick={() => toggleEnabled(entry.id)}
                                className="flex items-center gap-2 cursor-pointer"
                              >
                                <div
                                  className="relative w-8 h-4 rounded-full transition-colors duration-200"
                                  style={{
                                    background: entry.enabled
                                      ? "linear-gradient(90deg, #8a2be2, #ff6a00)"
                                      : "rgba(107,114,128,0.4)",
                                  }}
                                >
                                  <div
                                    className="absolute top-0.5 w-3 h-3 rounded-full bg-white transition-transform duration-200"
                                    style={{
                                      transform: entry.enabled
                                        ? "translateX(18px)"
                                        : "translateX(2px)",
                                    }}
                                  />
                                </div>
                                <span
                                  className="text-xs"
                                  style={{
                                    color: entry.enabled ? "#c084fc" : "#6b7280",
                                  }}
                                >
                                  {entry.enabled ? "Active" : "Paused"}
                                </span>
                              </button>
                            )}

                            <div className="flex items-center gap-1">
                              {!isEditing ? (
                                <button
                                  type="button"
                                  className="p-1.5 rounded-lg text-gray-500 hover:text-white transition-colors cursor-pointer"
                                  style={{ background: "rgba(255,255,255,0.04)" }}
                                  title="Edit"
                                  onClick={() => startEditEntry(entry)}
                                >
                                  <i className="ri-pencil-line text-sm" />
                                </button>
                              ) : null}
                              <button
                                type="button"
                                className="p-1.5 rounded-lg text-gray-500 hover:text-red-400 transition-colors cursor-pointer"
                                style={{ background: "rgba(255,255,255,0.04)" }}
                                title="Delete"
                                onClick={() => deleteEntry(entry.id)}
                              >
                                <i className="ri-delete-bin-line text-sm" />
                              </button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              })()
            )}
            </div>
          </SectionCard>
        </div>
      </PageContainer>
    </CoreLayout>
  );
}
